import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  Phone,
  ShieldCheck,
  User,
  X,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Button } from '@/components/learner/ui/Button'
import {
  requestRecoveryCode,
  verifyFindId,
  verifyPasswordReset,
  confirmPasswordReset,
  type RecoveryPurpose,
  type RecoveryStudent,
} from '@/api/accountRecovery'
import { useResendCooldown } from '@/hooks/useResendCooldown'
import { getApiErrorMessage } from '@/utils/apiError'
import {
  getRecoveryRateLimitCooldownSeconds,
  getRecoveryRateLimitMessage,
  isRecoveryDailyRateLimit,
} from '@/utils/recoveryRateLimit'
import { learnerHandle } from '@/utils/learnerEmail'

interface Props {
  isOpen: boolean
  onClose: () => void
  onContactAcademy?: () => void
}

type Step = 'input' | 'code' | 'result'

const CONTAINED_WIDTH = 'box-border min-w-0 w-full max-w-full'
const FIELD_LABEL =
  'mb-1.5 block text-lc-v3-body-2 font-medium text-lc-text-secondary'
const FIELD_SHELL =
  `${CONTAINED_WIDTH} flex h-12 items-center gap-3 rounded-xl border border-lc-disabled-border bg-lc-bg-primary px-4 transition-[border-color,box-shadow] focus-within:border-lc-key-500 focus-within:ring-2 focus-within:ring-lc-key-100`
const FIELD_INPUT =
  'min-w-0 flex-1 bg-transparent text-lc-v3-body-1 font-medium text-lc-text-primary outline-none placeholder:text-lc-text-disabled'

export function AccountRecoverySelfServiceModal({
  isOpen,
  onClose,
  onContactAcademy,
}: Props) {
  const { t } = useTranslation('learner')
  const [purpose, setPurpose] = useState<RecoveryPurpose>('find-id')
  const [step, setStep] = useState<Step>('input')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [handles, setHandles] = useState<string[]>([])
  const [resetToken, setResetToken] = useState('')
  const [students, setStudents] = useState<RecoveryStudent[]>([])
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [resetDone, setResetDone] = useState(false)
  const [dailyLimitReached, setDailyLimitReached] = useState(false)
  const {
    secondsLeft,
    startCooldown,
    clearCooldown,
    isBlocked: resendBlocked,
  } = useResendCooldown()
  const nameId = useId()
  const phoneId = useId()
  const codeId = useId()
  const studentId = useId()
  const passwordId = useId()

  if (!isOpen) return null

  const switchTab = (p: RecoveryPurpose) => {
    setPurpose(p)
    setStep('input')
    setError(null)
    setCode('')
    setHandles([])
    setStudents([])
    setSelectedStudent(null)
    setNewPassword('')
    setResetDone(false)
    setDailyLimitReached(false)
    clearCooldown()
  }

  const wrap = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      setBusy(false)
    }
  }

  const onSend = () =>
    wrap(async () => {
      try {
        await requestRecoveryCode(purpose, name, phone)
        setStep('code')
        setDailyLimitReached(false)
        startCooldown()
      } catch (e) {
        const rateLimitMessage = getRecoveryRateLimitMessage(e, t)
        if (rateLimitMessage) {
          if (isRecoveryDailyRateLimit(e)) {
            setDailyLimitReached(true)
          } else {
            const cooldown = getRecoveryRateLimitCooldownSeconds(e)
            if (cooldown) startCooldown(cooldown)
          }
          setError(rateLimitMessage)
          return
        }
        throw e
      }
    })

  const onVerify = () =>
    wrap(async () => {
      if (purpose === 'find-id') {
        const r = await verifyFindId(name, phone, code)
        setHandles(r.handles)
      } else {
        const r = await verifyPasswordReset(name, phone, code)
        setResetToken(r.resetToken)
        setStudents(r.students)
        setSelectedStudent(r.students.length === 1 ? r.students[0].organizationMemberId : null)
      }
      setStep('result')
    })

  const onConfirmReset = () =>
    wrap(async () => {
      if (selectedStudent == null) return
      await confirmPasswordReset(resetToken, selectedStudent, newPassword)
      setResetDone(true)
    })

  const tabClass = (active: boolean) =>
    `h-10 flex-1 rounded-[10px] text-lc-v3-body-2 font-semibold transition-[background-color,color,box-shadow] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500 ${
      active
        ? 'bg-lc-bg-primary text-lc-text-primary shadow-lc-v3-card'
        : 'text-lc-text-tertiary hover:text-lc-text-secondary'
    }`

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
    >
      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-black/40 backdrop-blur-[1px]"
        className="gap-0 overflow-hidden rounded-[24px] bg-lc-bg-primary p-0 text-lc-text-primary shadow-[0_8px_12px_rgba(0,0,0,0.12)] ring-0 sm:max-w-[400px]"
      >
        <div className={`${CONTAINED_WIDTH} p-8`}>
          <DialogHeader className="mb-6 flex-row items-center justify-between gap-4">
            <DialogTitle className="text-pretty text-lc-v3-h4 font-bold text-lc-text-primary">
              {t('auth.recovery.selfService.open')}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('auth.recovery.closeIconAriaLabel')}
              className="flex size-8 shrink-0 items-center justify-center rounded-lg text-lc-text-secondary hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </DialogHeader>

          {/* 세그먼트 탭: 아이디 찾기 / 비밀번호 재설정 */}
          <div className={`${CONTAINED_WIDTH} mb-6 flex gap-1 rounded-xl bg-lc-v3-gray-100 p-1`}>
            <button
              type="button"
              onClick={() => switchTab('find-id')}
              aria-pressed={purpose === 'find-id'}
              className={tabClass(purpose === 'find-id')}
            >
              {t('auth.recovery.selfService.tabFindId')}
            </button>
            <button
              type="button"
              onClick={() => switchTab('password-reset')}
              aria-pressed={purpose === 'password-reset'}
              className={tabClass(purpose === 'password-reset')}
            >
              {t('auth.recovery.selfService.tabResetPw')}
            </button>
          </div>

          {step === 'input' && (
            <div className="space-y-4">
              <div>
                <label htmlFor={nameId} className={FIELD_LABEL}>
                  {t('auth.recovery.selfService.name')}
                </label>
                <div className={FIELD_SHELL}>
                  <User className="size-5 shrink-0 text-lc-text-tertiary" aria-hidden />
                  <input
                    id={nameId}
                    name="studentName"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={FIELD_INPUT}
                  />
                </div>
              </div>
              <div>
                <label htmlFor={phoneId} className={FIELD_LABEL}>
                  {t('auth.recovery.selfService.guardianPhone')}
                </label>
                <div className={FIELD_SHELL}>
                  <Phone className="size-5 shrink-0 text-lc-text-tertiary" aria-hidden />
                  <input
                    id={phoneId}
                    name="guardianPhone"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="tel"
                    placeholder="01012345678"
                    className={FIELD_INPUT}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="primary"
                size="L"
                disabled={busy || !name || !phone}
                onClick={onSend}
                className={CONTAINED_WIDTH}
              >
                {t('auth.recovery.selfService.sendCode')}
              </Button>
            </div>
          )}

          {step === 'code' && (
            <div className="space-y-4">
              <div>
                <label htmlFor={codeId} className={FIELD_LABEL}>
                  {t('auth.recovery.selfService.code')}
                </label>
                <div className={FIELD_SHELL}>
                  <ShieldCheck className="size-5 shrink-0 text-lc-text-tertiary" aria-hidden />
                  <input
                    id={codeId}
                    name="recoveryCode"
                    autoComplete="one-time-code"
                    spellCheck={false}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className={`${FIELD_INPUT} tracking-[0.3em]`}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="primary"
                size="L"
                disabled={busy || !code}
                onClick={onVerify}
                className={CONTAINED_WIDTH}
              >
                {t('auth.recovery.selfService.verify')}
              </Button>
              <button
                type="button"
                disabled={busy || resendBlocked || dailyLimitReached}
                onClick={onSend}
                className={`${CONTAINED_WIDTH} rounded-lg py-1 text-center text-lc-v3-body-2 font-semibold text-lc-text-link hover:text-lc-text-link-hover hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500 disabled:text-lc-text-disabled`}
              >
                {resendBlocked
                  ? t('auth.recovery.selfService.resendIn', { seconds: secondsLeft })
                  : t('auth.recovery.selfService.resend')}
              </button>
            </div>
          )}

          {step === 'result' && purpose === 'find-id' && (
            <div className="space-y-4">
              <h3 className="text-lc-v3-body-1 font-semibold text-lc-text-primary">
                {t('auth.recovery.selfService.foundHandlesTitle')}
              </h3>
              <ul className="space-y-2">
                {handles.map((h) => (
                  <li
                    key={h}
                    className="select-all break-all rounded-xl bg-lc-v3-gray-100 px-4 py-3 text-lc-v3-body-1 font-semibold text-lc-text-primary"
                  >
                    {learnerHandle(h)}
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                variant="primary"
                size="L"
                onClick={onClose}
                className={CONTAINED_WIDTH}
              >
                {t('auth.recovery.selfService.done')}
              </Button>
            </div>
          )}

          {step === 'result' && purpose === 'password-reset' && !resetDone && (
            <div className="space-y-4">
              {students.length > 1 && (
                <div>
                  <label htmlFor={studentId} className={FIELD_LABEL}>
                    {t('auth.recovery.selfService.selectStudent')}
                  </label>
                  <select
                    id={studentId}
                    name="student"
                    value={selectedStudent ?? ''}
                    onChange={(e) => setSelectedStudent(Number(e.target.value))}
                    className={`${CONTAINED_WIDTH} h-12 rounded-xl border border-lc-disabled-border bg-lc-bg-primary px-4 text-lc-v3-body-1 font-medium text-lc-text-primary outline-none focus:border-lc-key-500 focus:ring-2 focus:ring-lc-key-100`}
                  >
                    <option value="" disabled>
                      {t('auth.recovery.selfService.selectStudent')}
                    </option>
                    {students.map((s) => (
                      <option key={s.organizationMemberId} value={s.organizationMemberId}>
                        {s.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label htmlFor={passwordId} className={FIELD_LABEL}>
                  {t('auth.recovery.selfService.newPassword')}
                </label>
                <div className={FIELD_SHELL}>
                  <Lock className="size-5 shrink-0 text-lc-text-tertiary" aria-hidden />
                  <input
                    id={passwordId}
                    name="newPassword"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    spellCheck={false}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={FIELD_INPUT}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="inline-flex size-8 items-center justify-center rounded-lg text-lc-text-tertiary hover:bg-lc-key-50 hover:text-lc-text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
                    aria-label={
                      showPw ? t('auth.password.hide') : t('auth.password.show')
                    }
                    aria-pressed={showPw}
                  >
                    {showPw ? (
                      <EyeOff className="size-[18px]" aria-hidden />
                    ) : (
                      <Eye className="size-[18px]" aria-hidden />
                    )}
                  </button>
                </div>
              </div>
              <Button
                type="button"
                variant="primary"
                size="L"
                disabled={busy || selectedStudent == null || !newPassword}
                onClick={onConfirmReset}
                className={CONTAINED_WIDTH}
              >
                {t('auth.recovery.selfService.confirmReset')}
              </Button>
            </div>
          )}

          {step === 'result' && purpose === 'password-reset' && resetDone && (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="mx-auto size-12 text-lc-text-success" aria-hidden />
              <p className="text-lc-v3-body-1 font-semibold text-lc-text-primary">
                {t('auth.recovery.selfService.done')}
              </p>
              <Button
                type="button"
                variant="primary"
                size="L"
                onClick={onClose}
                className={CONTAINED_WIDTH}
              >
                {t('auth.recovery.selfService.done')}
              </Button>
            </div>
          )}

          {error && (
            <p
              role="alert"
              className="mt-4 text-lc-v3-body-2 font-medium text-lc-text-error"
            >
              {error}
            </p>
          )}

          <p className="mt-6 text-center text-lc-v3-body-2 text-lc-text-secondary">
            {t('auth.recovery.selfService.fallbackHint')}{' '}
            <button
              type="button"
              className="rounded font-semibold text-lc-text-link underline hover:text-lc-text-link-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
              onClick={onContactAcademy}
            >
              {t('auth.recovery.selfService.fallbackLink')}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
